import { BlockStatus, RecordType, spansOverlap, type LiveTimeBlock } from '@wiztime/core';
import { ValidationError } from '../errors';
import type { WriteSession } from './session';

export const OverlapResolution = {
  KeepA: 'keepA',
  KeepB: 'keepB',
  Trim: 'trim',
} as const;
export type OverlapResolution = (typeof OverlapResolution)[keyof typeof OverlapResolution];

/** The block that the overlapping minutes count towards: greater `(countRank, id)` wins (the rule statistics use). */
function countedOf(a: LiveTimeBlock, b: LiveTimeBlock): { winner: LiveTimeBlock; loser: LiveTimeBlock } {
  const rankA = a.data.countRank ?? 0;
  const rankB = b.data.countRank ?? 0;
  const aWins = rankA !== rankB ? rankA > rankB : a.id > b.id;
  return aWins ? { winner: a, loser: b } : { winner: b, loser: a };
}

/**
 * Tidy up an overlap between two confirmed blocks of the same day (ui-redesign §2.1.8):
 * `keepA` deletes B, `keepB` deletes A. `trim` keeps both: the overlapping minutes stay with
 * the block with the greater `(countRank, id)` (the same rule statistics use); the other block
 * is cut back, split in two when the winner lies strictly inside it (the pieces copy its
 * `countRank`), or deleted when nothing is left. Drafts do not take part: they are not counted
 * by statistics, so they have no attribution to resolve.
 */
export async function resolveOverlap(
  session: WriteSession,
  idA: string,
  idB: string,
  mode: OverlapResolution,
): Promise<void> {
  const a = await session.require(RecordType.TimeBlock, idA);
  const b = await session.require(RecordType.TimeBlock, idB);
  if (a.data.status === BlockStatus.Draft || b.data.status === BlockStatus.Draft) {
    throw new ValidationError('Drafts do not take part in overlap resolution');
  }
  if (a.data.personalDate !== b.data.personalDate || !spansOverlap(a.data, b.data)) {
    throw new ValidationError('The two blocks do not overlap');
  }
  if (mode === OverlapResolution.KeepA) {
    await session.write(RecordType.TimeBlock, idB, null);
    return;
  }
  if (mode === OverlapResolution.KeepB) {
    await session.write(RecordType.TimeBlock, idA, null);
    return;
  }
  const { winner, loser } = countedOf(a, b);
  const pieces = [
    { startMin: loser.data.startMin, endMin: Math.min(loser.data.endMin, winner.data.startMin) },
    { startMin: Math.max(loser.data.startMin, winner.data.endMin), endMin: loser.data.endMin },
  ].filter((piece) => piece.startMin < piece.endMin);
  if (pieces.length === 0) {
    await session.write(RecordType.TimeBlock, loser.id, null);
    return;
  }
  for (const [index, piece] of pieces.entries()) {
    await session.write(RecordType.TimeBlock, index === 0 ? loser.id : session.newId(), {
      ...loser.data,
      ...piece,
    });
  }
}
